/**
 * Historical label resolution utility.
 *
 * ARCHITECTURE RULE: Historical UI must NEVER depend on active lookup collections
 * to resolve labels. When a master entity (treatment, status, condition, staff) is
 * archived it disappears from active maps — but historical rows must remain visible
 * and readable.
 *
 * Resolution order:
 *   1. entity    — live master entity (displayName → name); locale-resolved by backend
 *   2. snapshot  — fallback when entity is unavailable (archived/deleted from lookup map)
 *   3. fallback  — safe display string, never a raw UUID or undefined
 *
 * Usage:
 *   resolveHistoricalLabel({ snapshot, entity: treatmentObject })
 *   resolveHistoricalLabel({ snapshot, entity: statusObject, entityLabelField: 'name' })
 *   resolveHistoricalLabel({ snapshot, entity: staffObject, fallback: 'Archived staff' })
 */

/** Minimal structural shape required from a master entity. */
export interface EntityWithLabel {
    displayName?: string | null;
    name?: string | null;
}

export interface ResolveHistoricalLabelOptions {
    /** Value stored in the historical row at record-creation time. */
    snapshot?: string | null;
    /**
     * Live master entity. May be undefined/null when archived and not preloaded.
     * Must have at least one of `displayName` or `name`.
     */
    entity?: EntityWithLabel | null;
    /**
     * Which field to prefer on the entity.
     * If omitted the function tries 'displayName', then 'name', in that order.
     */
    entityLabelField?: 'displayName' | 'name';
    /** Shown when both snapshot and entity are unavailable. Default: '—'. */
    fallback?: string;
}

export function resolveHistoricalLabel(opts: ResolveHistoricalLabelOptions): string {
    const { snapshot, entity, entityLabelField, fallback = '—' } = opts;

    // Prefer live entity (locale-resolved displayName from backend) over snapshot.
    // Snapshot is only used when the entity is no longer in the lookup map (archived/deleted).
    if (entity) {
        if (entityLabelField) {
            const val = entity[entityLabelField];
            if (val) return val;
        }
        // Auto-detect: displayName preferred, then name
        if (entity.displayName) return entity.displayName;
        if (entity.name) return entity.name;
    }

    // Entity not available — fall back to the immutable snapshot stored at creation time
    if (snapshot) return snapshot;

    return fallback;
}
